import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Header } from '@/ui/components/Header';
import { Footer } from '@/ui/components/Footer';
import { MobilePageHeader } from '@/ui/components/MobilePageHeader';
import { SEO } from '@/ui/components/SEO';
import { UserX, ListOrdered, CreditCard, Eraser, Trash2, Archive } from 'lucide-react';

/**
 * Page « Supprimer votre compte » — /suppression-compte
 * URL déclarée à Google Play (Sécurité des données) : la procédure de suppression doit y figurer
 * de manière bien distincte, avec le nom de l'appli et les données supprimées / conservées.
 * Le chemin dans l'interface est recomposé à partir des libellés réels de l'écran « Mon compte »,
 * pour rester exact dans les 6 langues si ces libellés évoluent.
 */
const AccountDeletionPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isNative = Capacitor.isNativePlatform();

    const uiPath = [
        t('auth.account.title'),
        t('auth.account.securityTitle'),
        t('auth.account.rgpdTitle'),
        t('auth.account.rgpdDelete'),
    ].join(' → ');

    const listSections = [
        {
            icon: <Trash2 className="w-5 h-5" />,
            titleKey: 'accountDeletion.page.deletedTitle',
            items: [
                'accountDeletion.page.deletedAccount',
                'accountDeletion.page.deletedContent',
                'accountDeletion.page.deletedSessions',
                'accountDeletion.page.deletedSubscriptions',
            ],
        },
        {
            icon: <Archive className="w-5 h-5" />,
            titleKey: 'accountDeletion.page.retainedTitle',
            items: [
                'accountDeletion.page.retainedLedgers',
                'accountDeletion.page.retainedBackups',
                'accountDeletion.page.retainedPayments',
            ],
        },
    ];

    const cardClass = 'bg-white/[0.03] border border-white/10 rounded-xl p-6';
    const cardTitle = (icon: React.ReactNode, key: string) => (
        <div className="flex items-center gap-3 mb-4">
            <div className="text-primary">{icon}</div>
            <h2 className="text-lg font-semibold">{t(key)}</h2>
        </div>
    );

    return (
        <>
            <SEO title={t('accountDeletion.page.title')} description={t('accountDeletion.page.seoDescription')} />
            {isNative
                ? <MobilePageHeader title={t('accountDeletion.page.title')} icon="person_remove" showBackButton onBack={() => navigate(-1)} />
                : <Header />
            }
            <main className={`min-h-screen bg-[#0f2023] text-white ${isNative ? 'pt-20 pb-24' : 'pt-28 pb-20'}`}>
                <div className="max-w-3xl mx-auto px-6">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm mb-4">
                            <UserX className="w-4 h-4" />
                            {t('accountDeletion.page.badge')}
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold mb-4">{t('accountDeletion.page.title')}</h1>
                        <p className="text-slate-400 text-sm leading-relaxed">{t('accountDeletion.page.intro')}</p>
                    </div>

                    <div className="space-y-8">
                        {/* Procédure — mise en avant en premier (exigence Google Play) */}
                        <section className={`${cardClass} border-primary/30`}>
                            {cardTitle(<ListOrdered className="w-5 h-5" />, 'accountDeletion.page.stepsTitle')}
                            <ol className="space-y-3 list-decimal list-inside text-slate-300 text-sm leading-relaxed">
                                <li>{t('accountDeletion.page.stepInApp', { path: uiPath })}</li>
                                <li>{t('accountDeletion.page.stepConfirm')}</li>
                            </ol>
                            <p className="mt-4 text-slate-400 text-sm leading-relaxed">{t('accountDeletion.page.stepEmail')}</p>
                        </section>

                        <section className={cardClass}>
                            {cardTitle(<CreditCard className="w-5 h-5" />, 'accountDeletion.page.playTitle')}
                            <p className="text-slate-400 text-sm leading-relaxed">{t('auth.account.deletePlayWarning')}</p>
                        </section>

                        <section className={cardClass}>
                            {cardTitle(<Eraser className="w-5 h-5" />, 'accountDeletion.page.partialTitle')}
                            <p className="text-slate-400 text-sm leading-relaxed">{t('accountDeletion.page.partialText')}</p>
                            <p className="mt-3 text-slate-400 text-sm leading-relaxed">{t('accountDeletion.page.localText')}</p>
                        </section>

                        {listSections.map((section) => (
                            <section key={section.titleKey} className={cardClass}>
                                {cardTitle(section.icon, section.titleKey)}
                                <ul className="space-y-2">
                                    {section.items.map((itemKey) => (
                                        <li key={itemKey} className="text-slate-400 text-sm leading-relaxed flex items-start gap-2">
                                            <span className="text-primary/60 mt-1.5">•</span>
                                            <span>{t(itemKey)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ))}

                        <p className="text-center text-sm">
                            <Link to="/confidentialite" className="text-primary underline hover:text-primary/80">
                                {t('accountDeletion.page.privacyLink')}
                            </Link>
                        </p>
                    </div>
                </div>
            </main>
            {!isNative && <Footer />}
        </>
    );
};

export default AccountDeletionPage;
